import { getChartDefaultOptions } from "./ChartJsConfig";
import { useTheme } from "@mui/material/styles";
import appDefaultTheme from "@/config/muiTheme";
import { getChartTextColor } from "./chartTextColor";
import React, { useState, useEffect, useMemo, useContext } from "react";
import * as ReactChartJs from "react-chartjs-2";
import moment from "moment";

import { RPNumberFormat } from "@/helpers/utils";
import {
  getDatasetDefaultOptions,
  getAreaDatasetDefaultOptions,
} from "./ChartJsConfig";
import DrillControl from "./DrillControl";
import { WIDGETS_WITH_DRILL_FEATURE, DRILL_LEVELS } from "@/config/constants";
import {
  formatAndSortData,
  isBrandHasDrillFeature,
} from "@/features/campaigns/shared/helpers/drillUpDownHelper";
import { GlobalContext } from "@/helpers/contexts/contexts";

type ChartRow = Record<string, string | number | null | undefined>;

type DataFormat = {
  prefix: string;
  suffix: string;
};

type DataFormats = Record<string, DataFormat>;

type RangeDate = {
  year: string | number;
  month: string | number;
  date: string | number;
};

type ReportRange = {
  from: RangeDate;
  to: RangeDate;
};

type DrillValues = {
  filter?: string;
  customRange?: string[];
  group_id?: string | number;
  location_id?: string | number;
  channel_id?: string | number;
};

type AreaChartProps = {
  data?: ChartRow[];
  labels: string[];
  option?: Record<string, unknown>;
  scales?: Record<string, unknown>;
  title?: string;
  formats?: DataFormats | null;
  widgetId?: number;
  values?: DrillValues;
  initialRange?: ReportRange | null;
};

type TooltipItem = {
  datasetIndex: number;
  index: number;
};

type TooltipData = {
  datasets: Array<{
    label?: string;
    data?: Array<{ y?: string | number | null } | undefined>;
  }>;
};

const { Line } = ReactChartJs as unknown as {
  Line: React.ComponentType<Record<string, unknown>>;
};
const AreaChart = ({
  data = [],
  labels,
  option = {},
  scales = {},
  title = "",
  formats = null,
  widgetId,
  values,
  initialRange,
}: AreaChartProps) => {
  const { selectedBrand } = useContext(GlobalContext);

  const providedTheme = useTheme();
  const theme = providedTheme.palette.neutral ? providedTheme : appDefaultTheme;
  const [chartData, setChartData] = useState<Record<string, unknown>>({});
  const [drillLevel, setDrillLevel] = useState("month");

  const options = useMemo(
    () => ({
      ...getChartDefaultOptions(theme),
      tooltips: {
        ...getChartDefaultOptions(theme).tooltips,
        intersect: false,
        callbacks: {
          label: function (tooltipItem: TooltipItem, data: TooltipData) {
            const label =
              (data && data?.datasets[tooltipItem.datasetIndex].label) || "";

            const val =
              (data && data?.datasets[0]?.data?.[tooltipItem.index]) || {};

            const format = formats ? formats[labels[1]] : null;
            if (!val.y) return "";
            if (formats && format) {
              return `${label}: ${RPNumberFormat({
                prefix: format.prefix,
                suffix: format.suffix,
                thousand: ",",
              }).to(parseFloat(`${val.y}`))}`;
            }
            return `${label}: ${RPNumberFormat().to(parseFloat(`${val.y}`))}`;
          },
          title: (tooltipItem: Array<{ label: string }>) => {
            const label = tooltipItem[0].label;
            if (drillLevel === DRILL_LEVELS.DAY) {
              return moment(label, "YYYY-MM-DD").format("DD MMM YYYY");
            } else if (
              drillLevel === DRILL_LEVELS.QUARTER ||
              drillLevel === DRILL_LEVELS.YEAR
            ) {
              return label;
            } else {
              return moment(label, "YYYY-MM-DD").format("MMM YYYY");
            }
          },
        },
      },
      legend: {
        display: false,
        align: "start",
        labels: {
          boxWidth: 10,
          fontFamily: theme.typography.fontFamily,
          fontColor: getChartTextColor(theme),
          padding: 5,
          marginBottom: 10,
        },
      },
      plugins: {
        datalabels: {
          display: false,
        },
      },
      scales: {
        fontFamily: theme.typography.fontFamily,
        xAxes: [
          {
            gridLines: {
              display: false,
            },
            ticks: {
              fontColor: getChartTextColor(theme),
              callback: (value: string) => {
                if (
                  drillLevel === DRILL_LEVELS.QUARTER ||
                  drillLevel === DRILL_LEVELS.YEAR
                ) {
                  return value;
                } else if (drillLevel === DRILL_LEVELS.DAY) {
                  return moment(value, "YYYY-MM-DD").format("DD");
                } else {
                  return moment(value, "YYYY-MM-DD").format("MMM YYYY");
                }
              },
            },
          },
        ],
        yAxes: [
          {
            id: "y-axis-0",
            position: "left",
            ticks: {
              fontColor: getChartTextColor(theme),
              min: 0,
              maxTicksLimit: 5,
              callback: (value: string | number = 0) => {
                const format = formats ? formats[labels[1]] : null;

                if (!value) return "";
                if (formats && format) {
                  return RPNumberFormat({
                    prefix: format.prefix,
                    suffix: format.suffix,
                    thousand: ",",
                  }).to(value);
                }
                return RPNumberFormat().to(value);
              },
            },
          },
        ],
        ...scales,
      },

      ...option,
    }),
    [option, scales, drillLevel, theme]
  );

  const getDataSet = (
    data: ChartRow[] = [],
    currentDrillLevel = drillLevel
  ) => {
    const formatData = formatAndSortData(data, labels, currentDrillLevel);

    const datasets = [
      {
        label: title,
        data: formatData,
        yAxisID: "y-axis-0",
        ...getDatasetDefaultOptions(theme),
        ...getAreaDatasetDefaultOptions(theme),
        lineTension: 0.01,
      },
    ];
    return {
      labels: formatData?.map((d) => d.x),
      datasets: datasets,
    };
  };

  useEffect(() => {
    const initData = {
      //labels: getMonthLabels().map((l) => l.label),
      labels: getDataSet()[0]?.data
        ? getDataSet()[0]?.data?.map((d) => d.x)
        : [],
      datasets: getDataSet(),
    };
    setChartData(initData);
  }, [data]);

  useEffect(() => {
    setChartData(getDataSet(data));
  }, [data]);

  useEffect(() => {
    // A color refresh must preserve the report data and drill level on screen.
    setChartData((previous) => ({
      ...previous,
      datasets: Array.isArray(previous.datasets)
        ? previous.datasets.map((dataset) => ({
            ...dataset,
            ...getDatasetDefaultOptions(theme),
            ...getAreaDatasetDefaultOptions(theme),
          }))
        : [],
    }));
  }, [theme]);

  const handleDrillChange = (newDrillLevel: string, newData: unknown[]) => {
    setDrillLevel(newDrillLevel);
    setChartData(() => getDataSet(newData as ChartRow[], newDrillLevel));
  };

  return (
    <div className="area-chart">
      {isBrandHasDrillFeature(
        selectedBrand?.system_generated_brand_id as string | undefined
      ) &&
        widgetId &&
        WIDGETS_WITH_DRILL_FEATURE.includes(widgetId) && (
          <DrillControl
            drillLevel={drillLevel}
            onDrillChange={handleDrillChange}
            widgetId={widgetId}
            values={values || {}}
            initialRange={initialRange}
          />
        )}
      <Line data={chartData} options={options} />
    </div>
  );
};

export default AreaChart;
